import React, { useEffect, useState, useRef, useMemo } from "react";
import api from "@/services/api.js";
import toast from "react-hot-toast";
import { capture } from "@/instrument";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineX,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineUser,
  HiChevronLeft,
  HiOutlineIdentification,
  HiOutlineLocationMarker,
  HiOutlineDocumentText,
  HiOutlineExclamationCircle,
  HiOutlineRefresh,
  HiOutlineArrowsExpand,
} from "react-icons/hi";
import { cleanPhone, formatDateToFrench, formatBirthdate, formatINS, formatAddress, groupRenewalsByPrescription } from "@/utils";
import { useLoading } from "@/context/LoadingContext.jsx";

const RenewalsModal = ({ laboratoryId, patient, onClose, type }) => {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();
  const isMobile = useMemo(() => window.innerWidth < 768, []);
  const modalRef = useRef(null);

  const [step, setStep] = useState("contact");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [otpType, setOtpType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [renewals, setRenewals] = useState([]);
  const [selectedRenewalKeys, setSelectedRenewalKeys] = useState([]);
  const [lastSelectedKey, setLastSelectedKey] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [loadedImageUrls, setLoadedImageUrls] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectionToken, setSelectionToken] = useState("");
  const [otpError, setOtpError] = useState(false);

  const inputRefs = useRef([]);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);

  useEffect(() => {
    if (patient?.renewals?.[0]?.key) {
      setStep("select");
      setRenewals(patient.renewals);
    }
  }, [patient]);

  useEffect(() => {
    const groups = groupRenewalsByPrescription(renewals);
    const activeGroup = groups[Math.min(carouselIndex, groups.length - 1)];
    const preselected = activeGroup && activeGroup.renewals.length === 1 ? [activeGroup.renewals[0].key] : [];
    setSelectedRenewalKeys(preselected);
    setLastSelectedKey(preselected[0] ?? null);
  }, [renewals, carouselIndex]);

  const isEmailValid = (email) => {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  };

  const isPhoneValid = (phone) => {
    if (!phone) return false;
    return /^\+[1-9][0-9]{6,14}$/.test(cleanPhone(phone));
  };

  const sendOtp = async () => {
    if (!email && !phone) {
      toast.error("Veuillez renseigner votre email ou votre numéro de téléphone");
      return;
    }

    if (email && !isEmailValid(email)) {
      toast.error("Format d'email invalide");
      return;
    }

    if (phone && !isPhoneValid(phone)) {
      toast.error("Format de téléphone invalide");
      return;
    }

    try {
      setIsLoading(true);
      const sanitizedPhone = phone ? cleanPhone(phone) : null;
      const res = await api.post(`/patient/request-otp`, { type: email ? "email" : "phone", value: email || sanitizedPhone, laboratoryId });

      if (!res.ok) {
        if (res.code === "PATIENT_NOT_FOUND") {
          toast.error("Aucun compte trouvé avec ces informations");
        } else {
          toast.error("Une erreur est survenue lors de l'envoi du code");
        }
        return;
      }

      setOtpType(res.data.type);
      setStep("otp");
      toast.success(`Code envoyé par ${res.data.type === "email" ? "email" : "SMS"}`);
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors de l'envoi du code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      inputRefs.current[index + 1].focus();
    }

    if (newOtp.every((digit) => digit !== "")) {
      verifyOtp(newOtp.join(""));
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, otp.length);

    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split("").forEach((value, index) => {
      if (index < otp.length) {
        newOtp[index] = value;
      }
    });

    setOtp(newOtp);

    const lastFilledIndex = Math.min(pastedData.length - 1, otp.length - 1);
    inputRefs.current[lastFilledIndex].focus();

    if (newOtp.every((digit) => digit !== "")) {
      verifyOtp(newOtp.join(""));
    }
  };

  const verifyOtp = async (otpValue) => {
    try {
      setIsLoading(true);
      setOtpError(false);
      const sanitizedPhone = phone ? cleanPhone(phone) : null;
      const res = await api.post(`/patient/verify-otp`, {
        value: email || sanitizedPhone,
        type: email ? "email" : "phone",
        otp: otpValue,
        id: patient?._id,
        laboratoryId,
      });

      if (res.data?.candidates?.length > 0) {
        setCandidates(res.data.candidates);
        setSelectionToken(res.data.selectionToken);
        setStep("patient-select");
        return;
      }

      toast.error("Aucun profil trouvé avec ces informations");
    } catch (error) {
      if (error?.code === "OTP_INVALID") {
        setOtpError(true);
        setOtp(new Array(6).fill(""));
        if (inputRefs.current[0]) inputRefs.current[0].focus();
        return;
      }
      capture(error);
      toast.error("Une erreur est survenue lors de la vérification");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPatient = async (patientId) => {
    try {
      setIsLoading(true);

      const selectRes = await api.post(`/patient/select-account`, { selectedPatientId: patientId, selectionToken });
      if (!selectRes.ok) {
        toast.error("Une erreur est survenue lors de la sélection du profil");
        return;
      }

      sessionStorage.setItem("patientToken", selectRes.data.token);
      localStorage.setItem("rememberMe", selectRes.data.rememberMe);

      const meRes = await api.get(`/patient/me`);
      if (!meRes.ok) {
        toast.error("Une erreur est survenue lors de la récupération des informations");
        return;
      }

      if (meRes.data?.renewals?.length > 0) {
        setRenewals(meRes.data.renewals);
        setStep("select");
        return;
      }

      setStep("no-renewals");
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetryContact = () => {
    setEmail("");
    setPhone("");
    setOtp(new Array(6).fill(""));
    setCandidates([]);
    setOtpError(false);
    setStep("contact");
  };

  const onSelectRenewals = async () => {
    if (selectedRenewalKeys.length === 0) {
      toast.error("Veuillez sélectionner au moins une analyse");
      return;
    }

    try {
      startLoading();
      const formData = new FormData();
      formData.append("laboratoryId", laboratoryId);
      formData.append("renewalKeys", JSON.stringify(selectedRenewalKeys));
      if (type) formData.append("type", type);
      const res = await api.postFormData("/admission", formData);

      if (!res.ok) {
        toast.error(res.message || "Une erreur est survenue lors de la création de l'admission");
        return stopLoading();
      }

      if (res.auth) {
        sessionStorage.setItem("formToken", res.auth.formToken);
        sessionStorage.setItem("deviceId", res.auth.deviceId);
      }

      navigate(`/identification`);
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors de la création de l'admission");
      stopLoading();
    }
  };

  const renderContactStep = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Vérifiez votre identité</h2>
        <p className="text-sm text-gray-600">Entrez votre email ou numéro de téléphone pour accéder à vos ordonnances renouvelables</p>
      </div>

      <div className="space-y-3">
        <div>
          <label htmlFor="renewal-email" className="block text-sm font-medium text-gray-700 mb-1">
            Adresse Email
          </label>
          <div className="relative">
            <input
              autoFocus
              ref={emailRef}
              id="renewal-email"
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={patient?.email ? patient.email : "votre@email.com"}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <HiOutlineMail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">ou</span>
          </div>
        </div>

        <div>
          <label htmlFor="renewal-phone" className="block text-sm font-medium text-gray-700 mb-1">
            Numéro de téléphone
          </label>
          <div className="relative">
            <input
              ref={phoneRef}
              id="renewal-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={patient?.phone ? `${patient.phone}` : "+33 6 12 34 56 78"}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <HiOutlinePhone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>

      <button
        onClick={sendOtp}
        disabled={isLoading || (!email && !phone)}
        className="w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? "Envoi en cours..." : "Continuer"}
      </button>
    </div>
  );

  const renderOtpStep = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Confirmer que c'est vous</h2>
        <p className="text-sm text-gray-600">
          Entrez le code reçu par {otpType === "email" ? "email" : "SMS"} {otpType === "email" ? `(${email})` : `(${phone})`}
        </p>
        {otpType === "email" && !otpError && <p className="text-xs text-gray-500 mt-1">L'email peut être dans vos spams</p>}
      </div>

      {otpError && (
        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
          <HiOutlineExclamationCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Code incorrect</p>
            <p className="text-xs text-red-600 mt-0.5">Le code de vérification que vous avez saisi est invalide. Veuillez réessayer.</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-center">
        {otp.map((digit, index) => (
          <input
            key={index}
            autoFocus={index === 0 || (otpError && index === 0)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            ref={(el) => (inputRefs.current[index] = el)}
            onChange={(e) => {
              setOtpError(false);
              handleOtpChange(e, index);
            }}
            onKeyDown={(e) => handleOtpKeyDown(e, index)}
            onPaste={handleOtpPaste}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            className={`w-12 h-12 text-center border rounded-lg text-lg focus:outline-none focus:ring-2 transition-colors ${
              otpError ? "border-red-300 focus:border-red-500 focus:ring-red-200" : "border-gray-300 focus:border-primary focus:ring-primary"
            }`}
          />
        ))}
      </div>

      <div className="space-y-2">
        <button
          onClick={sendOtp}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 text-sm text-primary hover:text-primary-600 font-medium disabled:opacity-50"
        >
          <HiOutlineRefresh className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          Renvoyer le code
        </button>
        <button
          onClick={() => {
            setStep("contact");
            setOtp(new Array(6).fill(""));
            setOtpError(false);
          }}
          className="w-full text-sm text-gray-500 hover:text-gray-700"
        >
          Modifier l'email ou le téléphone
        </button>
      </div>
    </div>
  );

  const getCandidateMeta = (candidate) => {
    const { _id, firstName, lastName, birthdate, birthLastName, sexe, INS, address, zipCode, city, country, email: candidateEmail, phone: candidatePhone } = candidate;

    return {
      id: _id,
      firstName,
      lastName,
      birthLastName,
      displayName: [firstName, lastName].filter(Boolean).join(" ") || "Identité personnelle",
      initials: `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?",
      formattedBirthdate: formatBirthdate(birthdate),
      sexe,
      formattedINS: formatINS(INS?.value),
      formattedAddress: formatAddress({ address, zipCode, city, country }),
      email: candidateEmail,
      phone: candidatePhone ? cleanPhone(candidatePhone) : null,
    };
  };

  const CandidateDetails = ({ meta, className = "" }) => (
    <div className={`space-y-2.5 text-[13px] text-gray-600 ${className}`}>
      {meta.formattedBirthdate && (
        <div className="flex items-center gap-2.5">
          <HiOutlineIdentification className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{meta.formattedBirthdate}</span>
          {meta.sexe && <span className="text-gray-400">• {meta.sexe}</span>}
        </div>
      )}
      {meta.formattedINS && (
        <div className="flex items-center gap-2.5">
          <HiOutlineUser className="h-4 w-4 text-gray-400 shrink-0" />
          <span className="font-mono">{meta.formattedINS}</span>
        </div>
      )}
      {meta.formattedAddress && (
        <div className="flex items-start gap-2.5">
          <HiOutlineLocationMarker className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
          <span className="leading-relaxed">{meta.formattedAddress}</span>
        </div>
      )}
      {meta.email && (
        <div className="flex items-center gap-2.5">
          <HiOutlineMail className="h-4 w-4 text-gray-400 shrink-0" />
          <span className="truncate">{meta.email}</span>
        </div>
      )}
      {meta.phone && (
        <div className="flex items-center gap-2.5">
          <HiOutlinePhone className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{meta.phone}</span>
        </div>
      )}
    </div>
  );

  const renderPatientSelectStep = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Sélectionnez votre profil</h2>
        <p className="text-sm text-gray-600">
          {candidates.length === 1
            ? "Un profil correspond à vos informations. Confirmez qu'il s'agit bien de vous."
            : "Plusieurs profils correspondent à vos informations. Choisissez celui qui vous correspond."}
        </p>
      </div>

      <div className="space-y-3">
        {candidates.map((candidate) => {
          const meta = getCandidateMeta(candidate);
          const showBirthName = meta.birthLastName && meta.birthLastName !== meta.lastName;

          return (
            <button
              key={meta.id}
              type="button"
              onClick={() => handleSelectPatient(meta.id)}
              disabled={isLoading}
              className="group relative w-full rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 text-white text-base font-semibold shadow-sm">
                  {meta.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-900 group-hover:text-primary transition-colors">{meta.displayName}</p>
                  {showBirthName && <p className="text-xs text-gray-500 mt-0.5">Né(e) {meta.birthLastName}</p>}
                </div>
              </div>

              <CandidateDetails meta={meta} className="mt-4" />
            </button>
          );
        })}

        <button
          type="button"
          onClick={handleRetryContact}
          disabled={isLoading}
          className="group relative w-full rounded-2xl border-2 border-dashed border-gray-300 bg-white p-4 text-left hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
              <HiOutlineUser className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-gray-900 group-hover:text-primary transition-colors">Ce n'est pas le profil que je cherche</p>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">Retester avec un autre numéro de téléphone ou email</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  const renderNoRenewalsStep = () => (
    <div className="space-y-6">
      <div className="text-center pt-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-4">
          <HiOutlineDocumentText className="w-8 h-8 text-amber-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucune ordonnance renouvelable</h2>
        <p className="text-sm text-gray-600 max-w-sm mx-auto">
          Nous n'avons trouvé aucune ordonnance renouvelable associée à ce profil. Cela peut signifier que vos ordonnances ont déjà été utilisées ou qu'elles ne sont pas éligibles
          au renouvellement.
        </p>
      </div>

      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setStep("patient-select")}
          className="w-full px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-600 transition-colors"
        >
          Sélectionner un autre profil
        </button>
        <button
          type="button"
          onClick={handleRetryContact}
          className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
        >
          Essayer avec un autre email ou téléphone
        </button>
        <button type="button" onClick={onClose} className="w-full text-sm text-gray-500 hover:text-gray-700">
          Fermer
        </button>
      </div>
    </div>
  );

  const toggleRenewalKey = (renewalKey) => {
    setLastSelectedKey(renewalKey);
    setSelectedRenewalKeys((current) => (current.includes(renewalKey) ? current.filter((key) => key !== renewalKey) : [...current, renewalKey]));
  };

  const formatRenewalActs = (renewal) =>
    (renewal.acts || [])
      .map((act) => act.text || (act.codes || []).join(", "))
      .filter(Boolean)
      .join(", ");

  const formatRenewalMeta = (renewal) => {
    const made = renewal.nbMade || 0;
    const usage = renewal.nbRequested ? `${made}/${renewal.nbRequested} effectués` : `${made} effectué${made > 1 ? "s" : ""}`;
    const expiry = renewal.expiresAt ? `jusqu'au ${formatDateToFrench(new Date(renewal.expiresAt))}` : "sans limite";
    return `${usage} · ${expiry}`;
  };

  const pluralize = (count, singular, plural) => `${count} ${count > 1 ? plural : singular}`;

  const renderSelectStep = () => {
    const groups = groupRenewalsByPrescription(renewals);
    const activeGroup = groups[Math.min(carouselIndex, groups.length - 1)];
    const activeDoctor = `${(activeGroup.doctor?.firstName || []).join(" ")} ${activeGroup.doctor?.lastName || ""}`.trim();
    const selectedRenewals = renewals.filter((renewal) => selectedRenewalKeys.includes(renewal.key));
    const highlightedRenewal = activeGroup.renewals.find((renewal) => renewal.key === lastSelectedKey) || null;
    const expandedImageUrl = (highlightedRenewal && highlightedRenewal.s3Url) || activeGroup.renewals.map((renewal) => renewal.s3Url).find(Boolean);
    const onCarouselScroll = (event) => {
      const carousel = event.currentTarget;
      const cards = Array.from(carousel.children);
      const distances = cards.map((card) => Math.abs(card.offsetLeft - carousel.scrollLeft));
      const isScrolledToEnd = carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 1;
      setCarouselIndex(isScrolledToEnd ? cards.length - 1 : distances.indexOf(Math.min(...distances)));
    };
    const onCardSelect = (event, index) => {
      if (index === carouselIndex) return;
      setCarouselIndex(index);
      event.currentTarget.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    };
    const partsList = activeGroup.renewals.map((renewal) => (
      <label key={renewal.key} className="flex items-start gap-3 py-3 cursor-pointer">
        <input
          type="checkbox"
          checked={selectedRenewalKeys.includes(renewal.key)}
          onChange={() => toggleRenewalKey(renewal.key)}
          className="mt-0.5 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary accent-primary"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-medium text-gray-900">{formatRenewalActs(renewal) || "Analyses"}</div>
          <div className="text-xs text-gray-500 mt-0.5">{formatRenewalMeta(renewal)}</div>
        </div>
      </label>
    ));
    const continueButton = (
      <button
        onClick={onSelectRenewals}
        disabled={selectedRenewals.length === 0}
        className="w-full px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
      >
        {selectedRenewals.length > 1 ? `Continuer (${selectedRenewals.length})` : "Continuer"}
      </button>
    );

    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Ordonnances</h2>
          <p className="text-sm text-gray-500 mt-0.5">{pluralize(groups.length, "renouvelable", "renouvelables")}</p>
        </div>

        <div className="-mx-5 md:-mx-6">
          <div
            onScroll={onCarouselScroll}
            className="flex items-start gap-3.5 overflow-x-auto snap-x snap-mandatory scroll-pl-7 px-7 pb-5 pt-2"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {groups.map((candidate, index) => {
              const highlighted = candidate.renewals.find((renewal) => renewal.key === lastSelectedKey);
              const cardImageUrl = (highlighted && highlighted.s3Url) || candidate.renewals.map((renewal) => renewal.s3Url).find(Boolean);
              return (
                <div
                  key={candidate.key}
                  className={`relative shrink-0 snap-start bg-white overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.16)] ${cardImageUrl ? "" : "w-[78%] md:w-[58%] h-[21.5rem]"}`}
                >
                  <button
                    type="button"
                    onClick={(event) => (index === carouselIndex && cardImageUrl ? setExpanded(true) : onCardSelect(event, index))}
                    onFocus={(event) => onCardSelect(event, index)}
                    aria-label={candidate.date ? `Ordonnance du ${formatDateToFrench(new Date(candidate.date))}` : "Ordonnance"}
                    aria-current={index === carouselIndex}
                    className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                  >
                    {cardImageUrl ? (
                      <img
                        src={cardImageUrl}
                        alt=""
                        onLoad={() => setLoadedImageUrls((urls) => [...urls, cardImageUrl])}
                        className={`block ${loadedImageUrls.includes(cardImageUrl) ? "max-h-[21.5rem] max-w-[20rem]" : "h-[21.5rem] w-[15.2rem]"}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <HiOutlineDocumentText className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                  </button>
                  {index === carouselIndex && cardImageUrl && (
                    <button
                      type="button"
                      onClick={() => setExpanded(true)}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur shadow-sm flex items-center justify-center text-gray-900 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label="Agrandir l'ordonnance"
                    >
                      <HiOutlineArrowsExpand className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          {groups.length > 1 && (
            <div className="flex gap-1.5 mb-3">
              {groups.map((candidate, index) => (
                <span key={candidate.key} className={`h-1.5 rounded-full transition-all ${index === carouselIndex ? "w-[18px] bg-gray-900" : "w-1.5 bg-black/15"}`} />
              ))}
            </div>
          )}
          <div className="text-lg font-bold text-gray-900 tracking-tight truncate">
            {activeGroup.date ? `Ordonnance du ${formatDateToFrench(new Date(activeGroup.date))}` : "Ordonnance"}
          </div>
          <div className="text-sm text-gray-500 mt-0.5 truncate">{activeDoctor !== "" ? `Dr ${activeDoctor}` : "Médecin inconnu"}</div>
        </div>

        <div className="rounded-2xl border border-black/[0.07] bg-white px-4 divide-y divide-gray-100">{partsList}</div>

        {continueButton}

        {expanded && expandedImageUrl && (
          <div className="fixed inset-0 z-[60] bg-black flex flex-col">
            <div className="flex items-center gap-3 px-4 pt-4 pb-3 text-white">
              <button type="button" onClick={() => setExpanded(false)} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0" aria-label="Fermer">
                <HiChevronLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <div className="text-[15px] font-semibold truncate">{activeGroup.date ? `Ordonnance du ${formatDateToFrench(new Date(activeGroup.date))}` : "Ordonnance"}</div>
                <div className="text-xs text-white/70 truncate">{activeDoctor !== "" ? `Dr ${activeDoctor}` : ""}</div>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <img src={expandedImageUrl} alt="" className="w-full max-w-3xl mx-auto" />
            </div>
            <div className="bg-white rounded-t-3xl shadow-[0_-6px_20px_rgba(0,0,0,0.2)] px-4 pt-3 pb-5 max-h-[58vh] flex flex-col">
              <div className="w-full max-w-3xl mx-auto flex flex-col min-h-0">
                <div className="overflow-y-auto divide-y divide-gray-100">{partsList}</div>
                <div className="pt-2">{continueButton}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} />
        <div ref={modalRef} className="fixed bottom-0 left-0 right-0 z-50" style={{ transition: "transform 0.3s ease-out", touchAction: "none" }}>
          <div className="bg-white rounded-t-3xl shadow-2xl px-5 pt-4 pb-6 max-h-[90vh] overflow-y-auto">
            <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-300 mb-4" />
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <HiOutlineX className="w-5 h-5" />
            </button>

            {step === "contact" && renderContactStep()}
            {step === "otp" && renderOtpStep()}
            {step === "patient-select" && renderPatientSelectStep()}
            {step === "no-renewals" && renderNoRenewalsStep()}
            {step === "select" && renderSelectStep()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-h-[90vh] overflow-y-auto max-w-lg">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10">
            <HiOutlineX className="w-5 h-5" />
          </button>

          <div className="p-6">
            {step === "contact" && renderContactStep()}
            {step === "otp" && renderOtpStep()}
            {step === "patient-select" && renderPatientSelectStep()}
            {step === "no-renewals" && renderNoRenewalsStep()}
            {step === "select" && renderSelectStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalsModal;
